import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  FaTwitter,
  FaGoogle,
  FaYoutube,
  FaFacebook,
  FaInstagram,
  FaSearch,
  FaUser,
  FaHeart,
  FaShoppingCart,
  FaBars,
  FaTimes,
  FaChevronDown,
  FaLinkedin,
  FaGlobe,
} from "react-icons/fa";
import { mobileMenu, navMenu } from "../data/products";
import { useStore } from "../context/Storecontext";
import ProductsMenu from "./ProductsMenu";
import MobileProductsMenu from "./MobileProductsMenu";
import axios from "axios";
import ImageWithSkeleton from "./ImageWithSkeleton";

// ─── Search suggestion config ───────────────────────────────────────────────
const API_BASE =
  (typeof import.meta !== "undefined" && import.meta.env?.VITE_API_BASE_URL) ||
  process.env?.REACT_APP_API_BASE_URL ||
  "http://localhost:8000/api";
const STORAGE_BASE = API_BASE.replace("/api", "") + "/storage/";

function getProductImage(product) {
  const img = product.primary_image || product.images?.[0];
  if (!img) return null;
  const src = img.image || img;
  return src.startsWith("http") ? src : STORAGE_BASE + src;
}

// ─── SearchDropdown sub-component ───────────────────────────────────────────
function SearchDropdown({ query, onSelect, onClose, className, style }) {
  const [suggestions, setSuggestions] = useState({
    categories: [],
    products: [],
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!query.trim()) {
      setSuggestions({ categories: [], products: [] });
      return;
    }

    setLoading(true);
    const lower = query.toLowerCase();

    // Fetch both categories and products in parallel
    Promise.all([
      axios
        .get(`${API_BASE}/categorilist`)
        .catch(() => ({ data: { data: [] } })),
      axios
        .get(`${API_BASE}/productlist`)
        .catch(() => ({ data: { data: [] } })),
    ]).then(([catRes, prodRes]) => {
      const allCats = catRes.data?.data || catRes.data || [];
      const allProds = prodRes.data?.data || prodRes.data || [];

      // Match categories
      const matchedCats = allCats
        .filter((c) => c.name?.toLowerCase().includes(lower))
        .slice(0, 4);

      // Match products — also include products whose category matches
      const matchedCatIds = new Set(
        matchedCats.map((c) => c.category_id || c.id),
      );
      const matchedProds = allProds
        .filter(
          (p) =>
            p.name?.toLowerCase().includes(lower) ||
            // p.description?.toLowerCase().includes(lower) ||
            matchedCatIds.has(p.category_id),
        )
        .slice(0, 6);

      setSuggestions({ categories: matchedCats, products: matchedProds });
      setLoading(false);
    });
  }, [query]);

  const hasResults =
    suggestions.categories.length > 0 || suggestions.products.length > 0;

  if (!query.trim()) return null;

  return (
    <div
      className={
        className ||
        "absolute left-0 top-[calc(100%+10px)] w-80 bg-white border border-gray-200 shadow-xl z-50 rounded-sm overflow-hidden"
      }
      style={style || { minWidth: "320px" }}
    >
      {loading && (
        <div className="flex items-center gap-2 px-4 py-3 text-xs text-gray-400">
          <span className="w-3 h-3 border-2 border-gray-200 border-t-[#43a047] rounded-full animate-spin inline-block" />
          Searching…
        </div>
      )}

      {!loading && !hasResults && (
        <div className="px-4 py-3 text-xs text-gray-400 italic">
          No results for &ldquo;{query}&rdquo;
        </div>
      )}

      {!loading && suggestions.categories.length > 0 && (
        <div>
          <div className="px-4 pt-3 pb-1 text-[9px] font-bold uppercase tracking-widest text-gray-400 border-b border-gray-100">
            Categories
          </div>
          {suggestions.categories.map((cat) => (
            <button
              key={cat.category_id || cat.id}
              onMouseDown={() => onSelect("category", cat)}
              className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition-colors text-left group"
            >
              <span className="w-5 h-5 rounded-full bg-[#e8f5e9] flex items-center justify-center flex-shrink-0">
                <svg
                  className="w-2.5 h-2.5 text-[#43a047]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              </span>
              <span className="text-xs text-gray-700 group-hover:text-[#43a047] transition-colors font-medium">
                {cat.name}
              </span>
              {cat.parent_id && (
                <span className="ml-auto text-[9px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-sm">
                  Sub
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {!loading && suggestions.products.length > 0 && (
        <div
          className={
            suggestions.categories.length > 0 ? "border-t border-gray-100" : ""
          }
        >
          <div className="px-4 pt-3 pb-1 text-[9px] font-bold uppercase tracking-widest text-gray-400 border-b border-gray-100">
            Products
          </div>
          {suggestions.products.map((prod) => {
            const img = getProductImage(prod);
            return (
              <button
                key={prod.product_id}
                onMouseDown={() => onSelect("product", prod)}
                className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition-colors text-left group"
              >
                <div className="w-8 h-8 border border-gray-100 rounded-sm overflow-hidden flex-shrink-0 bg-gray-50 flex items-center justify-center">
                  {img ? (
                    <ImageWithSkeleton
                      src={img}
                      alt={prod.name}
                      containerClassName="w-full h-full"
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <span className="text-gray-300 text-[10px]">—</span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-700 group-hover:text-[#43a047] transition-colors truncate font-medium">
                    {prod.name}
                  </p>
                  {prod.category?.name && (
                    <p className="text-[9px] text-gray-400 truncate mt-0.5">
                      in {prod.category.name}
                    </p>
                  )}
                </div>
                {prod.is_best_seller && (
                  <span className="ml-auto text-[8px] font-bold bg-[#112244] text-white px-1 py-0.5 rounded-sm flex-shrink-0">
                    BEST
                  </span>
                )}
                {prod.is_new_arrival && !prod.is_best_seller && (
                  <span className="ml-auto text-[8px] font-bold bg-[#43a047] text-white px-1 py-0.5 rounded-sm flex-shrink-0">
                    NEW
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* See all results footer */}
      {!loading && hasResults && (
        <div className="border-t border-gray-100">
          <button
            onMouseDown={() => onSelect("all", { query })}
            className="w-full px-4 py-2.5 text-[11px] text-[#43a047] font-semibold hover:bg-[#f1f8f1] transition-colors flex items-center justify-center gap-1"
          >
            <FaSearch size={9} />
            See all results for &ldquo;{query}&rdquo;
          </button>
        </div>
      )}
    </div>
  );
}
// ─────────────────────────────────────────────────────────────────────────────

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMenus, setOpenMenus] = useState({});
  const [cartOpen, setCartOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);
  const [mobileSearchQuery, setMobileSearchQuery] = useState("");
  const [mobileSearchFocused, setMobileSearchFocused] = useState(false);
  const mobileSearchRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { cart, cartCount, cartSubtotal, removeFromCart } = useStore();
  const searchRef = useRef(null);

  const toggleMenu = (label) => {
    setOpenMenus((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  const isMenuItemActive = (item) => {
    if (item.href === "/" && location.pathname === "/") {
      return true;
    }
    if (
      item.href !== "/" &&
      item.href !== "#" &&
      location.pathname === item.href
    ) {
      return true;
    }
    if (item.children) {
      return item.children.some((child) => location.pathname === child.href);
    }
    return false;
  };

  const handleSearch = () => {
    if (searchQuery.trim()) {
      window.__searched = true;
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`, {
        state: { fromSearch: true },
      });
      setSearchQuery("");
      setSearchFocused(false);
    }
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
    if (e.key === "Escape") {
      setSearchFocused(false);
      setSearchQuery("");
    }
  };

  const handleMobileSearch = () => {
    if (mobileSearchQuery.trim()) {
      window.__searched = true;
      navigate(`/search?q=${encodeURIComponent(mobileSearchQuery.trim())}`, {
        state: { fromSearch: true },
      });
      setMobileSearchQuery("");
      setMobileOpen(false);
    }
  };

  const handleMobileSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      handleMobileSearch();
    }
  };

  // Handle suggestion selection
  const handleSuggestionSelect = (type, item) => {
    setSearchFocused(false);
    if (type === "product") {
      setSearchQuery("");
      navigate(`/product-details/${item.product_id}`);
    } else if (type === "category") {
      const q = item.name;
      setSearchQuery(q);
      window.__searched = true;
      navigate(`/search?q=${encodeURIComponent(q)}`, {
        state: { fromSearch: true },
      });
      setSearchQuery("");
    } else if (type === "all") {
      window.__searched = true;
      navigate(`/search?q=${encodeURIComponent(item.query)}`, {
        state: { fromSearch: true },
      });
      setSearchQuery("");
    }
  };

  const handleMobileSuggestionSelect = (type, item) => {
    setMobileSearchFocused(false);
    setMobileOpen(false);
    if (type === "product") {
      setMobileSearchQuery("");
      navigate(`/product-details/${item.product_id}`);
    } else if (type === "category") {
      const q = item.name;
      setMobileSearchQuery(q);
      window.__searched = true;
      navigate(`/search?q=${encodeURIComponent(q)}`, {
        state: { fromSearch: true },
      });
      setMobileSearchQuery("");
    } else if (type === "all") {
      window.__searched = true;
      navigate(`/search?q=${encodeURIComponent(item.query)}`, {
        state: { fromSearch: true },
      });
      setMobileSearchQuery("");
    }
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setSearchFocused(false);
      }
      if (
        mobileSearchRef.current &&
        !mobileSearchRef.current.contains(e.target)
      ) {
        setMobileSearchFocused(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <>
      {/* Mobile Offcanvas Overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Mobile Offcanvas */}
      <div
        className={`fixed top-0 left-0 h-full w-72 bg-white text-gray-800 z-50 transform transition-transform duration-300 ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        } lg:hidden overflow-y-auto shadow-xl`}
      >
        {/* Use flex column configuration to push email content to the bottom */}
        <div className="flex flex-col h-full p-5">
          {/* Close Button Header Row */}
          <div className="flex justify-end mb-4">
            <button
              className="text-gray-500 hover:text-red-500 transition-colors p-1"
              onClick={() => setMobileOpen(false)}
            >
              <FaTimes size={20} />
            </button>
          </div>

          {/* Search Input Bar for Mobile */}
          <div ref={mobileSearchRef} className="relative mb-5 flex-shrink-0">
            <div className="flex items-center border border-gray-300 rounded overflow-hidden">
              <input
                type="text"
                placeholder="Search product..."
                className="w-full bg-transparent text-black text-sm outline-none px-3 py-2"
                value={mobileSearchQuery}
                onChange={(e) => {
                  setMobileSearchQuery(e.target.value);
                  setMobileSearchFocused(true);
                }}
                onKeyDown={handleMobileSearchKeyDown}
                onFocus={() => setMobileSearchFocused(true)}
              />
              <button
                onClick={handleMobileSearch}
                className="btn-readmore border border-2-black text-gray p-2.5 flex items-center justify-center transition-colors border-none rounded-none"
                style={{ minWidth: "45px" }}
              >
                <FaSearch size={15} />
              </button>
            </div>

            {/* Suggestion dropdown for Mobile */}
            {mobileSearchFocused && mobileSearchQuery.trim() && (
              <SearchDropdown
                query={mobileSearchQuery}
                onSelect={handleMobileSuggestionSelect}
                onClose={() => setMobileSearchFocused(false)}
                className="absolute left-0 top-[calc(100%+5px)] w-full bg-white border border-gray-200 shadow-xl z-50 rounded-sm overflow-y-auto max-h-72"
                style={{ minWidth: "100%" }}
              />
            )}
          </div>

          {/* Customer Support Info Block */}
          {/* <div className="flex items-center gap-3 mb-6 px-1 flex-shrink-0">
            <div className="w-12 h-12 rounded-full border-2 border-[#43a047]/30 flex items-center justify-center bg-transparent flex-shrink-0">
              <div className="w-9 h-9 rounded-full border border-[#43a047] flex items-center justify-center text-[#43a047] bg-white">
                <svg
                  className="w-4.5 h-4.5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.94.725l.548 2.2a1 1 0 01-.321.988l-1.305.98a10.582 10.582 0 004.872 4.872l.98-1.305a1 1 0 01.988-.321l2.2.548a1 1 0 01.725.94V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                  />
                </svg>
              </div>
            </div>
            <div className="min-w-0">
              <div className="text-base font-bold text-gray-800 leading-tight">
               +91 8999718146
              </div>
              <div className="text-[11px] text-gray-500 font-semibold uppercase tracking-wider">
                Customer Support
              </div>
            </div>
          </div> */}

          {/* Mobile Navigation Links */}
          <nav className="flex-1">
            <div className="border-b border-gray-100">
              <Link
                to="/"
                onClick={() => setMobileOpen(false)}
                className={`block py-3.5 text-sm font-medium transition-colors ${
                  location.pathname === "/"
                    ? "text-[#43a047]"
                    : "text-gray-700 hover:text-[#43a047]"
                }`}
              >
                Home
              </Link>
            </div>
            <MobileProductsMenu onLinkClick={() => setMobileOpen(false)} />

            {mobileMenu.map((item) => {
              const isMenuOpen = openMenus[item.label];
              return (
                <div key={item.label} className="border-b border-gray-100">
                  <div className="flex items-center justify-between py-3.5">
                    <Link
                      to={item.href}
                      onClick={() => setMobileOpen(false)}
                      className={`text-sm font-medium transition-colors ${
                        isMenuItemActive(item)
                          ? "text-[#43a047]"
                          : "text-gray-700 hover:text-[#43a047]"
                      }`}
                    >
                      {item.label}
                    </Link>

                    {item.children && (
                      <button
                        onClick={() => toggleMenu(item.label)}
                        className="p-1 text-gray-400 hover:text-gray-600"
                      >
                        <FaChevronDown
                          size={11}
                          className={`transition-transform duration-200 ${
                            isMenuOpen ? "rotate-180 text-[#43a047]" : ""
                          }`}
                        />
                      </button>
                    )}
                  </div>

                  {item.children && isMenuOpen && (
                    <div className="pb-3 pl-4 space-y-1 bg-gray-50/50 rounded-sm px-2">
                      {item.children.map((child) => (
                        <div
                          key={child.label}
                          className="flex items-center justify-between py-2 border-b border-gray-100/50 last:border-0"
                        >
                          <Link
                            to={child.href}
                            onClick={() => setMobileOpen(false)}
                            className={`text-xs font-normal transition-colors ${
                              location.pathname === child.href
                                ? "text-[#43a047] font-medium"
                                : "text-gray-600 hover:text-[#43a047]"
                            }`}
                          >
                            {child.label}
                          </Link>
                          <FaChevronDown size={9} className="text-gray-400" />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Products mega menu — mobile version */}
          </nav>

          {/* Contact Information Bottom Block */}
          <div className="pt-4 border-t border-gray-100 mt-auto flex flex-col items-center justify-center gap-2 text-sm text-gray-600">
            <a
              href="mailto:info@yourdomain.com"
              className="hover:text-[#43a047] flex items-center gap-2 transition-colors py-2"
            >
              {/* Enclosed in a template expression to properly group text alongside markup inside template layout nodes */}
              <span className="inline-block text-gray-500">
                {/* Note: Ensure your icon import matches or replace with a custom inline SVG / alternative icon helper */}
                <svg
                  className="w-4 h-4 inline"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 002-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              </span>
              info@yourdomain.com
            </a>

            {/* Mobile Social Media Icons */}
            <div className="flex items-center gap-4 mt-2">
              {[
                {
                  icon: FaFacebook,
                  url: "https://www.facebook.com/people/Shree-Ambika-Global/61591070396765/ ",
                },
                {
                  icon: FaLinkedin,
                  url: "https://www.linkedin.com/in/nalin-bhalara-15b5213a8/ ",
                },
                { icon: FaGlobe, url: "https://ambikaglobal.linker.store/" },
              ].map((social, i) => (
                <a
                  key={i}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-primary text-base p-1 transition-colors"
                >
                  <social.icon />
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>

      <header className="sticky top-0 z-30 bg-white shadow-sm">
        {/* Top bar */}
        <div className="bg-gray-100 border-b border-border-color py-1.5 hidden lg:block">
          <div className="max-w-7xl mx-auto px-4 flex items-center justify-between">
            {/* Language / Currency */}
            <div className="flex items-center gap-4">
              {/* <div className="relative group cursor-pointer">
                  <span className="text-xs flex items-center gap-1 hover:text-primary">
                    Language <FaChevronDown size={9} />
                  </span>
                  <div className="absolute top-full left-0 mt-1 bg-white border border-border-color shadow-md hidden group-hover:block z-20 min-w-[100px]">
                    {["French", "Spanish", "Russian"].map((l) => (
                      <a
                        key={l}
                        href="#"
                        className="block px-3 py-1.5 text-xs hover:bg-light-gray hover:text-primary"
                      >
                        {l}
                      </a>
                    ))}
                  </div>
                </div> */}
              {/* <div className="relative group cursor-pointer">
                  <span className="text-xs flex items-center gap-1 hover:text-primary">
                    Currency <FaChevronDown size={9} />
                  </span>
                  <div className="absolute top-full left-0 mt-1 bg-white border border-border-color shadow-md hidden group-hover:block z-20 min-w-[110px]">
                    {["€ Euro", "£ Pound Sterling", "$ US Dollar"].map((c) => (
                      <a
                        key={c}
                        href="#"
                        className="block px-3 py-1.5 text-xs hover:bg-light-gray hover:text-primary"
                      >
                        {c}
                      </a>
                    ))}
                  </div>
                </div> */}
            </div>
            {/* Social */}
            {/* Social */}
            <div className="flex justify-center relative h-0 z-50">
              <Link to="/" className="absolute -top-5">
                <img
                  src={`${process.env.PUBLIC_URL}/img/logo/Logo5.png`}
                  alt="Safira"
                  className="h-[170px] max-w-none object-contain drop-shadow-sm transition-transform"
                />
              </Link>
            </div>

            <div className="flex items-center gap-3">
              {[
                {
                  icon: FaFacebook,
                  url: "https://www.facebook.com/people/Shree-Ambika-Global/61591070396765/ ",
                },
                {
                  icon: FaLinkedin,
                  url: "https://www.linkedin.com/in/nalin-bhalara-15b5213a8/ ",
                },
                { icon: FaGlobe, url: "https://ambikaglobal.linker.store/" }, // Using FaGlobe as a placeholder for Global Linker
              ].map((social, i) => (
                <a
                  key={i}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-primary text-2xl"
                >
                  <social.icon />
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Middle */}
        {/* <div className="py-3 lg:py-5 border-b border-border-color bg-[#CAE4C9] text-black"> */}
        {/* Middle */}
        <div className="py-2 h-[100px] lg:py-5 border-b border-border-color bg-[#CAE4C9] text-black">
          <div className="max-w-7xl mx-auto px-4 relative">
            {/* Mobile Layout */}
            <div className="flex lg:hidden items-center justify-between">
              {/* Logo */}
              <Link to="/" className=" flex items-center">
                <img
                  src={`${process.env.PUBLIC_URL}/img/logo/logo3-removebg-preview.png`}
                  alt="Safira"
                  className="h-[80px] w-auto object-contain"
                />
              </Link>
              {/* Right Icons */}
              <div className="flex items-center gap-4">
                <button
                  className="lg:hidden"
                  onClick={() => setMobileOpen(true)}
                >
                  <FaBars size={20} />
                </button>
              </div>
            </div>

            {/* Desktop Layout */}
            <div className="hidden lg:grid lg:grid-cols-3 lg:items-center">
              {/* Search — with live suggestion dropdown */}
              <div className="flex justify-start text-black">
                <div className="flex justify-start">
                  <div ref={searchRef} className="relative w-full max-w-xs">
                    {/* Underline input row */}
                    <div className="border-b-2 border-black mt-5 pb-1 flex items-center">
                      <input
                        type="text"
                        placeholder="Search here..."
                        className="w-full bg-transparent text-black placeholder-black/60 text-sm outline-none pr-7 pl-1 py-1"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyDown={handleSearchKeyDown}
                        onFocus={() => setSearchFocused(true)}
                      />
                      <button
                        onClick={handleSearch}
                        className="absolute right-1 text-black hover:text-gray-700 transition-colors bg-transparent p-0"
                      >
                        <FaSearch size={14} className="stroke-[1.5]" />
                      </button>
                    </div>

                    {/* Suggestion dropdown */}
                    {searchFocused && (
                      <SearchDropdown
                        query={searchQuery}
                        onSelect={handleSuggestionSelect}
                        onClose={() => setSearchFocused(false)}
                      />
                    )}
                  </div>
                </div>
              </div>

              {/* Perfect Center Logo */}

              {/* Right Side */}
              {/* <div className="flex items-center justify-end gap-4">
                  <div className="flex items-center gap-1 text-xs text-white">
                    <Link to="/login">Register</Link>
                    <span>/</span>
                    <Link to="/login">Login</Link>
                  </div>

                  <Link
                    to="/wishlist"
                    className="flex items-center gap-1 text-white"
                  >
                    <FaHeart size={16} />
                    <span className="text-xs">Wishlist</span>
                  </Link>

                  <button
                    onClick={() => setCartOpen(!cartOpen)}
                    className="flex items-center gap-1 text-white"
                  >
                    <FaShoppingCart size={18} />

                    <span className="bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                      {cartCount}
                    </span>

                    <span className="text-xs">Cart</span>
                  </button>
                </div> */}
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="hidden lg:block bg-white border-b border-border-color">
          <div className="max-w-7xl mx-auto px-4">
            <ul className="flex items-center">
              <li>
                <Link
                  to="/"
                  className={`flex items-center gap-1 text-sm font-medium px-4 py-3 transition-colors ${
                    location.pathname === "/"
                      ? "text-primary"
                      : "text-dark hover:text-primary"
                  }`}
                >
                  Home
                </Link>
              </li>
              <ProductsMenu onLinkClick={() => {}} />
              {navMenu.map((item) => (
                <li key={item.label} className="relative group">
                  <Link
                    to={item.href}
                    className={`flex items-center gap-1 text-sm font-medium px-4 py-3 transition-colors ${
                      isMenuItemActive(item)
                        ? "text-primary"
                        : "text-dark hover:text-primary"
                    }`}
                  >
                    {item.label}
                    {item.children && <FaChevronDown size={9} />}
                  </Link>
                  {item.children && (
                    <div className="absolute top-full left-0 bg-white border border-border-color shadow-lg min-w-[180px] hidden group-hover:block z-20">
                      {item.children.map((child) => (
                        <Link
                          key={child.label}
                          to={child.href}
                          className={`block px-4 py-2 text-xs transition-colors ${
                            location.pathname === child.href
                              ? "text-primary font-medium"
                              : "text-dark hover:bg-light-gray hover:text-primary"
                          }`}
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </li>
              ))}

              {/* Products mega menu — desktop */}
            </ul>
          </div>
        </nav>
      </header>
    </>
  );
}
